import classNames from "classnames/bind";
import { sortBy } from "es-toolkit/compat";
import React from "react";
import styles from "./SelectPopover.module.scss";
import { SelectPopoverOptionType } from "./types";

const cx = classNames.bind(styles);

export type SeparatorProps = {
  /** does this separator have a label? */
  label?: string;
  /** are the options this separates part of a group? */
  isGrouping?: boolean;
};

export const Separator = ({ label, isGrouping }: SeparatorProps) => {
  if (label) {
    return (
      <div className={cx("separator-container", isGrouping && "group")}>
        <p className={cx("separator-label")}>{label}</p>
        {!isGrouping ? <hr className={cx("separator")} /> : null}
      </div>
    );
  }

  return <hr className={cx("separator")} />;
};

export const sortOptionsByPinned = (opts: SelectPopoverOptionType[]) =>
  // sort while preserving order. sort by unpinned last, then pinned above it
  sortBy(
    // clone the array to avoid mutation
    opts.slice(),
    [
      (opt) => {
        return !opt.isPinned;
      },
    ]
  );

export const handleKeyDown = ({
  evt: { code },
  actionHandlerMap,
}: {
  evt: React.KeyboardEvent<HTMLElement>;
  actionHandlerMap: Record<string, () => void>;
}) => actionHandlerMap[code]?.();

export const handleOptionKeyDownArrowUp = (
  evt: React.KeyboardEvent<HTMLElement>,
  currentIdx: number
) => {
  const optElements = [
    ...((evt.target as HTMLElement)?.closest("[role='listbox']")?.children ||
      []),
  ].filter((el) => el.role === "option") as HTMLElement[];

  if (currentIdx === 0) {
    return;
  }

  optElements[currentIdx - 1]?.focus();
};

export const handleOptionKeyDownArrowDown = (
  evt: React.KeyboardEvent<HTMLElement>,
  currentIdx: number
) => {
  const optElements = [
    ...((evt.target as HTMLElement)?.closest("[role='listbox']")?.children ||
      []),
  ].filter((el) => el.role === "option") as HTMLElement[];

  if (currentIdx === optElements.length - 1) {
    return;
  }

  optElements[currentIdx + 1]?.focus();
};

export const getSelectableOptions = (options: SelectPopoverOptionType[]) => {
  return options.reduce<SelectPopoverOptionType[]>((opts, o) => {
    if (o.options) {
      opts.push(...getSelectableOptions(o.options));
      return opts;
    }

    opts.push(o);
    return opts;
  }, []);
};

export const getMappedSelectableOptions = (
  options: SelectPopoverOptionType[],
  condition?: (opt: SelectPopoverOptionType) => boolean
) => {
  return options.reduce<string[]>((opts, o) => {
    if (o.options) {
      opts.push(...getMappedSelectableOptions(o.options, condition));
      return opts;
    }

    if ((condition && condition(o)) || !condition) opts.push(o.value);
    return opts;
  }, []);
};

/**
 * Get an array of indexes that recursively lead to the specified opt in a recursable list of opts
 * @param optToFind the opt to find in a recursable list of opts
 * @param allOpts a recursable list of opts
 * @returns an array of indexes if the opt to find has been found or an empty array otherwise
 */
export const getPositionOfOpt = (
  optToFind: SelectPopoverOptionType,
  allOpts: SelectPopoverOptionType[]
): number[] => {
  let isFound = false;

  const buildPositionPath = (
    opts: SelectPopoverOptionType[],
    positionPathProp?: number[]
  ) => {
    let positionPath = positionPathProp ? [...positionPathProp] : [];

    if (isFound) return positionPath;

    let idx = 0;

    for (const opt of opts) {
      if (isFound) break;

      if (opt.value === optToFind.value) {
        positionPath.push(idx);
        isFound = true;
        break;
      }

      if (opt.options) {
        // temporarily place the current opt's idx as we're looking under this opt
        positionPath.push(idx);

        const pathCandidate = buildPositionPath(opt.options, positionPath);

        // if the candidate's length is greater, it's been found
        if (pathCandidate.length > positionPath.length) {
          positionPath = [...pathCandidate];
          isFound = true;
          break;
        } else {
          // otherwise, remove the temporarily placed idx, as we didn't find the
          // opt under this opt at this idx
          positionPath.pop();
        }
      }

      idx++;
    }

    return positionPath;
  };

  return buildPositionPath(allOpts);
};

export const searchForOpt = (
  searchTerm: string,
  allOpts: SelectPopoverOptionType[]
) => {
  const sanitizedSearch = searchTerm.trim().toLowerCase();
  const doesSearchTermMatch = (opt: SelectPopoverOptionType) =>
    opt.label.trim().toLowerCase().includes(sanitizedSearch);

  const createNewOpts = (
    optsSubject: SelectPopoverOptionType[],
    depth = 0
  ): SelectPopoverOptionType[] => {
    return optsSubject.reduce<SelectPopoverOptionType[]>((opts, opt) => {
      // if there are any children, we need to eagerly look in there first
      if (opt.options) {
        // if the group matches, include the whole group
        if (doesSearchTermMatch(opt)) {
          opts.push(opt);
          return opts;
        }

        const foundOpts = createNewOpts(opt.options, depth + 1);

        if (!foundOpts.length) return opts;

        opts.push({
          ...opt,
          options: foundOpts,
        });
      } else if (doesSearchTermMatch(opt)) {
        opts.push(opt);
      }

      return opts;
    }, []);
  };

  return createNewOpts(allOpts, 0);
};

export const pinOption = (
  optToPin: SelectPopoverOptionType,
  allOpts: SelectPopoverOptionType[],
  forceShouldPin?: boolean
): SelectPopoverOptionType[] => {
  const shouldPin = !optToPin?.isPinned;

  return allOpts.map((opt) => {
    if (typeof forceShouldPin !== "undefined") {
      if (opt.options) {
        return {
          ...opt,
          isPinned: shouldPin,
          options: pinOption(optToPin, opt.options, forceShouldPin),
        };
      }

      return { ...opt, isPinned: forceShouldPin };
    }

    // is this the opt to pin?
    if (opt.value === optToPin.value) {
      // force pin all child columns if they exist..
      if (opt.options) {
        return {
          ...opt,
          isPinned: shouldPin,
          options: pinOption(
            optToPin,
            opt.options,
            forceShouldPin ?? shouldPin
          ),
        };
      }

      // or just this one
      return {
        ...opt,
        isPinned: shouldPin,
      };
    }

    return opt;
  });
};

export const moveOptionUp = (
  optToMove: SelectPopoverOptionType,
  allOpts: SelectPopoverOptionType[]
): SelectPopoverOptionType[] => {
  const posPathOfOptToMove = getPositionOfOpt(optToMove, allOpts);

  // if the opt to move is at the 0th index wherever in the opts tree it is, then we can't move it up
  if (posPathOfOptToMove[posPathOfOptToMove.length - 1] === 0) {
    return [...allOpts];
  }

  const createNewOpts = (
    optsSubject: SelectPopoverOptionType[],
    depth = 0
  ): SelectPopoverOptionType[] => {
    return optsSubject.reduce<SelectPopoverOptionType[]>((opts, opt, idx) => {
      // if this isn't the depth where the opt to move is
      if (depth !== posPathOfOptToMove.length - 1) {
        // if it is part of the path and child options exist, then dive deeper
        if (posPathOfOptToMove[depth] === idx && opt.options) {
          opts.push({
            ...opt,
            options: createNewOpts([...opt.options], depth + 1),
          });
        } else {
          // in all other cases, include the opt as normal
          opts.push(opt);
          return opts;
        }
      } else {
        // if this is at the same depth where the opt to move is too..
        // is the next index the same idx as the opt to move up?
        if (idx + 1 === posPathOfOptToMove[depth]) {
          // swap the next one with the current one and push both
          opts.push(optsSubject[posPathOfOptToMove[depth]], opt);
        } else if (idx !== posPathOfOptToMove[depth]) {
          // for all other indexes at the same depth, include the opt as normal
          opts.push(opt);
        }
      }

      return opts;
    }, []);
  };

  return createNewOpts(allOpts, 0);
};

export const moveOptionDown = (
  optToMove: SelectPopoverOptionType,
  allOpts: SelectPopoverOptionType[]
): SelectPopoverOptionType[] => {
  const posPathOfOptToMove = getPositionOfOpt(optToMove, allOpts);

  const createNewOpts = (
    optsSubject: SelectPopoverOptionType[],
    depth = 0
  ): SelectPopoverOptionType[] => {
    return optsSubject.reduce<SelectPopoverOptionType[]>((opts, opt, idx) => {
      // at the final depth and the opt to move is at the end of the list? you can't move it down
      if (
        depth === posPathOfOptToMove.length - 1 &&
        posPathOfOptToMove[depth] === optsSubject.length - 1
      ) {
        opts.push(opt);
        return opts;
      }

      // if this isn't the depth where the opt to move is
      if (depth !== posPathOfOptToMove.length - 1) {
        // if it is part of the path and child options exist, then dive deeper
        if (posPathOfOptToMove[depth] === idx && opt.options) {
          opts.push({
            ...opt,
            options: createNewOpts([...opt.options], depth + 1),
          });
        } else {
          // in all other cases, include the opt as normal
          opts.push(opt);
          return opts;
        }
      } else {
        // if this is at the same depth where the opt to move is too..
        // is the current idx the same idx as the opt to move down?
        if (idx === posPathOfOptToMove[depth]) {
          // swap the current one with the next one and push both
          opts.push(optsSubject[posPathOfOptToMove[depth] + 1], opt);
        } else if (idx !== posPathOfOptToMove[depth] + 1) {
          // for all other indexes at the same depth, include the opt as normal
          opts.push(opt);
        }
      }

      return opts;
    }, []);
  };

  return createNewOpts(allOpts, 0);
};
